import React, { useState } from "react";
import { Agent } from "../../../services/types";
import Modal from "./Modal";
import AdminService from "../../../services/admin.services";
import CreateSuspension from "src/pages/settings/components/CreateSuspension";
import ConfirmationModal from "src/components/ui/ConfirmationModal";
import {
  FiX,
  FiUser,
  FiMail,
  FiPhone,
  FiCalendar,
  FiShield,
  FiClock,
  FiToggleLeft,
  FiToggleRight,
} from "react-icons/fi";

const adminService = new AdminService();

interface AgentDetailsProps {
  agent: Agent;
  onStatusChange: (newStatus: string) => void;
}

const AgentDetails: React.FC<AgentDetailsProps> = ({
  agent,
  onStatusChange,
}) => {
  const [status, setStatus] = useState(() => {
    if (agent.is_suspended) return "suspended";
    if (!agent.is_checked_by_admin) return "inactive";
    return "active";
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: "",
    message: "",
    type: "success" as "success" | "error",
    action: () => {},
  });
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isSuspending, setIsSuspending] = useState(false);

  const handleToggleStatus = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowDeactivateModal(true);
  };

  const confirmToggleStatus = async () => {
    setIsDeactivating(true);
    try {
      const response = await adminService.checkAgent({ email: agent.email });
      const newStatus = status === "active" ? "inactive" : "active";
      const actionMessage =
        newStatus === "active" ? "activated" : "deactivated";

      setModalContent({
        title: `${
          newStatus.charAt(0).toUpperCase() + newStatus.slice(1)
        } Agent`,
        message:
          response.success ||
          `Agent ${agent.first_name} ${agent.last_name} ${actionMessage} successfully.`,
        type: "success",
        action: () => {
          setStatus(newStatus);
          onStatusChange(newStatus);
          setIsModalOpen(false);
        },
      });
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error toggling agent status:", error);
      setModalContent({
        title: "Error",
        message: `Failed to ${
          status === "active" ? "deactivate" : "activate"
        } agent. ${error instanceof Error ? error.message : ""}`,
        type: "error",
        action: () => setIsModalOpen(false),
      });
      setIsModalOpen(true);
    } finally {
      setShowDeactivateModal(false);
      setIsDeactivating(false);
    }
  };

  const handleSuspension = async () => {
    if (agent.is_suspended) {
      try {
        const response = await adminService.updateSuspensionStatus(agent._id);
        setModalContent({
          title: response?.success ? "Suspension Removed" : "Error",
          message: response?.success
            ? "Agent suspension removed successfully."
            : "Failed to update suspension status.",
          type: response?.success ? "success" : "error",
          action: () => setIsModalOpen(false),
        });
        setIsModalOpen(true);
      } catch (error) {
        setModalContent({
          title: "Error",
          message: "An error occurred while updating suspension status.",
          type: "error",
          action: () => setIsModalOpen(false),
        });
        setIsModalOpen(true);
      }
    } else {
      setShowSuspendModal(true);
    }
  };

  const confirmSuspendAgent = () => {
    setIsSuspending(true);
    setShowSuspendModal(false);
    setShowSuspensionModal(true);
    setIsSuspending(false);
  };

  const getStatusColor = () => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "suspended":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
      {/* Header with cover photo */}
      <div className="relative h-48 bg-gradient-to-r from-blue-500 to-purple-600">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center p-6 text-white">
            <h1 className="text-3xl font-bold">
              {agent.first_name} {agent.last_name}
            </h1>
            <p className="opacity-90">{agent.agent_type || "Agent"}</p>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="p-6 md:p-8">
        {/* Profile section */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8">
          <div className="relative -mt-20">
            <img
              src={agent.image_url || "/default-avatar.png"}
              alt={`${agent.first_name} ${agent.last_name}`}
              className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
            />
            <span
              className={`absolute bottom-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}
            >
              {status === "active"
                ? "Active"
                : status === "suspended"
                ? "Suspended"
                : "Inactive"}
            </span>
          </div>

          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold text-gray-800">
              {agent.first_name} {agent.last_name}
            </h2>
            <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-4">
              <div className="flex items-center text-gray-600">
                <FiMail className="mr-2" />
                <span>{agent.email}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <FiPhone className="mr-2" />
                <span>{agent.phone || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <DetailCard
            icon={<FiCalendar className="w-5 h-5" />}
            title="Date of Birth"
            value={agent.date_of_birth || "N/A"}
          />
          <DetailCard
            icon={<FiUser className="w-5 h-5" />}
            title="Agent Type"
            value={agent.agent_type || "N/A"}
          />
          <DetailCard
            icon={<FiShield className="w-5 h-5" />}
            title="Status"
            value={
              status === "active"
                ? "Active"
                : status === "suspended"
                ? "Suspended"
                : "Inactive"
            }
          />
          <DetailCard
            icon={<FiClock className="w-5 h-5" />}
            title="Last Login"
            value={agent.last_login || "N/A"}
          />
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleToggleStatus}
            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              status === "active"
                ? "bg-red-100 text-red-700 hover:bg-red-200"
                : "bg-green-100 text-green-700 hover:bg-green-200"
            }`}
          >
            {agent.is_verified ? (
              <>
                <FiToggleLeft className="w-5 h-5" />
                Deactivate
              </>
            ) : (
              <>
                <FiToggleRight className="w-5 h-5" />
                Activate
              </>
            )}
          </button>

          <button
            onClick={handleSuspension}
            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              agent.is_suspended
                ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                : "bg-red-100 text-red-700 hover:bg-red-200"
            }`}
          >
            {agent.is_suspended ? "Remove Suspension" : "Suspend Agent"}
          </button>
        </div>
      </div>

      {/* Modals */}
      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        onConfirm={confirmToggleStatus}
        title={agent.is_verified ? "Deactivate Agent" : "Activate Agent"}
        message={
          agent.is_verified
            ? "Are you sure you want to deactivate this agent? They will no longer be able to access the platform."
            : "Are you sure you want to activate this agent? They will regain access to the platform."
        }
        confirmText={agent.is_verified ? "Deactivate" : "Activate"}
        cancelText="Cancel"
        type="danger"
        entityName={`${agent.first_name} ${agent.last_name}`}
        entityType="agent"
        isLoading={isDeactivating}
        loadingText={agent.is_verified ? "Deactivating..." : "Activating..."}
      />

      <ConfirmationModal
        isOpen={showSuspendModal}
        onClose={() => setShowSuspendModal(false)}
        onConfirm={confirmSuspendAgent}
        title="Suspend Agent"
        message="Are you sure you want to suspend this agent? They will be temporarily blocked from accessing the platform."
        confirmText="Suspend"
        cancelText="Cancel"
        type="warning"
        entityName={`${agent.first_name} ${agent.last_name}`}
        entityType="agent"
        isLoading={isSuspending}
        loadingText="Processing..."
      />

      <Modal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalContent.title}
        message={modalContent.message}
        type={modalContent.type}
      />

      {showSuspensionModal && (
        <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full relative">
            <button
              onClick={() => setShowSuspensionModal(false)}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100"
            >
              <FiX className="w-5 h-5 text-gray-500" />
            </button>
            <CreateSuspension
              suspendeeId={agent._id}
              type="rider"
              onClose={() => setShowSuspensionModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for detail cards
const DetailCard = ({
  icon,
  title,
  value,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
}) => (
  <div className="bg-gray-50 p-4 rounded-lg">
    <div className="flex items-center gap-3 mb-2">
      <div className="text-gray-500">{icon}</div>
      <h3 className="font-medium text-gray-700">{title}</h3>
    </div>
    <p className="text-gray-900 pl-8">{value}</p>
  </div>
);

export default AgentDetails;
